if (!customElements.get('maison-athlete-deck')) {
  customElements.define('maison-athlete-deck', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.cards = [...this.querySelectorAll('.mad__card')];
      this.buttons = this.cards.map(card => card.querySelector('.mad__select'));
      this.desktop = matchMedia('(min-width: 1000px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.active = 0;
      this.paused = false;
      this.visible = false;
      this.hovered = false;
      this.focused = false;
      this.cards.forEach((card, index) => {
        card.addEventListener('pointermove', event => {
          if (!this.desktop.matches || event.pointerType === 'touch') return;
          const position = `${event.clientX},${event.clientY}`;
          if (this.pointerPosition !== position && this.active !== index) this.select(index);
          this.pointerPosition = position;
        }, options);
        this.buttons[index].addEventListener('click', () => this.select(index), options);
        this.buttons[index].addEventListener('keydown', event => {
          let next;
          if (['ArrowRight', 'ArrowDown'].includes(event.key)) next = (index + 1) % this.cards.length;
          if (['ArrowLeft', 'ArrowUp'].includes(event.key)) next = (index - 1 + this.cards.length) % this.cards.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.cards.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          this.select(next);
          this.buttons[next].focus();
        }, options);
      });
      this.addEventListener('pointerenter', () => { this.hovered = true; this.rotate(); }, options);
      this.addEventListener('pointerleave', () => { this.hovered = false; this.rotate(); }, options);
      this.addEventListener('focusin', () => { this.focused = true; this.rotate(); }, options);
      this.addEventListener('focusout', event => { this.focused = this.contains(event.relatedTarget); this.rotate(); }, options);
      this.desktop.addEventListener('change', () => { this.select(0); this.measure(); }, options);
      this.reduced.addEventListener('change', () => this.rotate(), options);
      document.addEventListener('visibilitychange', () => this.rotate(), options);
      document.addEventListener('shopify:block:select', event => {
        const index = this.cards.findIndex(card => card.dataset.blockId === event.detail.blockId);
        if (index >= 0) { this.paused = true; this.select(index); }
      }, options);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.rotate();
      });
      this.observer.observe(this);
      this.resize = new ResizeObserver(entries => {
        const width = entries[0].contentRect.width;
        if (this.measuredWidth !== width) { this.measuredWidth = width; this.measure(); }
      });
      this.resize.observe(this.querySelector('.mad__deck'));
      this.classList.add('is-ready');
      this.select(0);
      this.measure();
      document.fonts.ready.then(() => { if (this.isConnected) this.measure(); });
    }
    select(index) {
      this.active = index;
      this.cards.forEach((card, i) => {
        const expanded = i === index;
        card.classList.toggle('is-active', i === index);
        this.buttons[i].setAttribute('aria-expanded', String(expanded));
        const quote = card.querySelector('.mad__quote');
        quote.setAttribute('aria-hidden', String(!expanded));
        quote.inert = !expanded;
      });
      this.rotate();
    }
    measure() {
      if (!this.desktop.matches || !this.cards.length) return;
      const deck = this.querySelector('.mad__deck');
      const available = deck.clientWidth - 16 - (this.cards.length - 1) * 8;
      const expanded = available / (this.cards.length + .55) * 1.55;
      const width = expanded - 40;
      this.style.setProperty('--mad-image-width', `${expanded}px`);
      this.style.setProperty('--mad-copy-width', `${Math.max(1, width)}px`);
      const height = Math.max(...this.cards.map(card => card.querySelector('.mad__quote').scrollHeight));
      this.style.setProperty('--mad-copy-height', `${height}px`);
      // Measure at the narrowest width so headings never change the row height mid-transition.
      const resting = available / (this.cards.length + .55) - 40;
      this.style.setProperty('--mad-name-width', `${Math.max(1, resting)}px`);
      let nameHeight = 44;
      this.buttons.forEach(button => {
        const clone = button.cloneNode(true);
        Object.assign(clone.style, { position: 'absolute', visibility: 'hidden', width: `${resting}px` });
        button.parentElement.append(clone);
        nameHeight = Math.max(nameHeight, clone.offsetHeight);
        clone.remove();
      });
      this.style.setProperty('--mad-name-height', `${nameHeight}px`);
    }
    rotate() {
      clearInterval(this.timer);
      const enabled = this.dataset.autoplay === 'true' && this.cards.length > 1 && this.desktop.matches && !this.reduced.matches;
      if (!enabled || !this.visible || this.paused || this.hovered || this.focused || document.hidden || window.Shopify?.designMode) return;
      this.timer = setInterval(() => this.select((this.active + 1) % this.cards.length), 8000);
    }
    disconnectedCallback() {
      clearInterval(this.timer);
      this.controller?.abort();
      this.observer?.disconnect();
      this.resize?.disconnect();
    }
  });
}
